// lib/authLock.ts
// Cross-tab single-flight auth (login/refresh) coordination.
// Uses localStorage with a short TTL to avoid deadlocks on crashes.
// Pattern: acquireAuthLock('login') before starting; releaseAuthLock after.

const LOCK_KEY_PREFIX = 'auth_lock_';
const DEFAULT_TTL_MS = 15_000; // 15s guard window

interface LockPayload {
  owner: string; // typically device_id or random tab id
  expires_at: number; // epoch ms
  purpose: string; // 'login' | 'refresh'
}

function now() { return Date.now(); }

function readLock(purpose: string): LockPayload | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY_PREFIX + purpose);
    if (!raw) return null;
    const obj = JSON.parse(raw) as LockPayload;
    if (!obj || typeof obj !== 'object') return null;
    if (typeof obj.expires_at !== 'number' || obj.expires_at < now()) {
      // expired -> cleanup
      localStorage.removeItem(LOCK_KEY_PREFIX + purpose);
      return null;
    }
    return obj;
  } catch { return null; }
}

export function acquireAuthLock(purpose: 'login' | 'refresh', owner: string, ttlMs: number = DEFAULT_TTL_MS): boolean {
  const existing = readLock(purpose);
  if (existing && existing.expires_at > now()) {
    // Another tab holds it
    return false;
  }
  const payload: LockPayload = { owner, purpose, expires_at: now() + ttlMs };
  try { localStorage.setItem(LOCK_KEY_PREFIX + purpose, JSON.stringify(payload)); } catch { return false; }
  return true;
}

export function renewAuthLock(purpose: 'login' | 'refresh', owner: string, ttlMs: number = DEFAULT_TTL_MS): boolean {
  const existing = readLock(purpose);
  if (!existing || existing.owner !== owner) return false;
  existing.expires_at = now() + ttlMs;
  try { localStorage.setItem(LOCK_KEY_PREFIX + purpose, JSON.stringify(existing)); return true; } catch { return false; }
}

export function releaseAuthLock(purpose: 'login' | 'refresh', owner: string) {
  const existing = readLock(purpose);
  if (!existing) return;
  if (existing.owner !== owner) return; // don't remove someone else's
  try { localStorage.removeItem(LOCK_KEY_PREFIX + purpose); } catch { /* ignore */ }
}

export function waitForAuthLock(purpose: 'login' | 'refresh', timeoutMs = 20_000): Promise<'acquired' | 'timeout'> {
  const start = now();
  return new Promise(resolve => {
    const interval = setInterval(() => {
      const lk = readLock(purpose);
      if (!lk) { clearInterval(interval); resolve('acquired'); return; }
      if (now() - start > timeoutMs) { clearInterval(interval); resolve('timeout'); return; }
    }, 250);
  });
}
